import * as Sentry from "@sentry/react";
import { PropTypes } from "prop-types";
import React, { useEffect, useState } from "react";
import { NOOP_FALSE } from "~/constants/callbacks";
import { mlsFetch } from "~/lib/fetchClient";
import Select from "./SelectWrapper/Select";

const MultipleListingServicesSelect = ({
  id = "",
  name = "",
  value = "",
  placeholder = "Select a MLS",
  onChange = NOOP_FALSE,
  isInvalid = false,
  isClearable = false,
  isSearchable = false,
  primaryMls = false,
  teamMlses = false,
}) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const shouldShowTooltip = teamMlses && options.length === 0;
  const tooltipText = shouldShowTooltip ? (
    <>
      Go to MLS Settings{" "}
      <a
        className="text-success"
        href="/data"
        target="_blank"
        rel="noopener noreferrer"
      >
        here
      </a>{" "}
      to set your MLSs for this account
    </>
  ) : null;
  const tooltipPlacement = shouldShowTooltip ? "bottom" : "";
  const resource = teamMlses ? "/team_mlses/options" : "/mlses";

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    const optionsReq = mlsFetch.get(resource);
    const primaryReq =
      primaryMls && !value
        ? mlsFetch.get("/team_mlses/primary_mls_option")
        : null;

    const loadData = async () => {
      try {
        const [optionsBody, primaryBody] = await Promise.all([
          optionsReq.catch(() => null),
          (primaryReq || Promise.resolve(null)).catch((err) => {
            if (!cancelled && err?.name !== "AbortError") {
              Sentry.captureException(
                err instanceof Error
                  ? err
                  : new Error("Primary MLS option fetch failed"),
              );
            }
            return null;
          }),
        ]);

        if (cancelled) {
          return;
        }

        if (optionsBody) {
          setOptions(
            optionsBody.map((mls) => ({
              label: mls.name,
              value: mls.ouid,
            })),
          );
        }

        if (primaryBody) {
          onChange({ value: primaryBody.ouid, label: primaryBody.name });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
      optionsReq.abort();
      if (primaryReq) {
        primaryReq.abort();
      }
    };
  }, []);

  return (
    <Select
      validateValue
      id={id}
      name={name}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isInvalid={isInvalid}
      isLoading={isLoading}
      isClearable={isClearable}
      isSearchable={isSearchable}
      tooltipText={tooltipText}
      tooltipPlacement={tooltipPlacement}
    />
  );
};

MultipleListingServicesSelect.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.shape({}),
  ]),
  isInvalid: PropTypes.bool,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  primaryMls: PropTypes.bool,
  teamMlses: PropTypes.bool,
};

export default MultipleListingServicesSelect;
